import React, { ChangeEvent, FormEvent } from "react";
import { Checkbox, CheckboxProps, Form } from "semantic-ui-react";
import { FormattedMessage, useIntl } from "react-intl";
import MainButton from "../../../components/Button/MainButton";
import TextField from "../../../components/FormComponents/TextField";
import styles from "./UserForm.module.scss";

type Props = {
  data: {
    name: string;
    email: string;
    password?: string;
    password2?: string;
    isAdmin?: boolean;
  };
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onCheckboxChange: (
    e: React.FormEvent<HTMLInputElement>,
    data: CheckboxProps
  ) => void;
  edit?: boolean;
  isSuspended?: boolean;
  suspendReason?: string;
  onSuspendChange?: (checked: boolean) => void;
  onSuspendReasonChange?: (reason: string) => void;
  isBanned?: boolean;
  banReason?: string;
  onBanChange?: (checked: boolean) => void;
  onBanReasonChange?: (reason: string) => void;
};

const UserForm: React.FC<Props> = ({
  data: { name, email, password, password2, isAdmin },
  onSubmit,
  onChange,
  edit = false,
  onCheckboxChange,
  isSuspended = false,
  suspendReason = "",
  onSuspendChange,
  onSuspendReasonChange,
  isBanned = false,
  banReason = "",
  onBanChange,
  onBanReasonChange,
}) => {
  const intl = useIntl();
  const reasonInputStyle = (enabled: boolean): React.CSSProperties => ({
    display: "block",
    marginTop: "6px",
    width: "100%",
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "10px 12px",
    fontSize: "14px",
    opacity: enabled ? 1 : 0.5,
    cursor: enabled ? "text" : "not-allowed",
    boxSizing: "border-box",
  });

  return (
    <Form onSubmit={onSubmit}>
      <Form.Field>
        <TextField
          name="name"
          placeholder={intl.formatMessage({
            id: "register.form.username.placeholder",
            defaultMessage: "Username",
          })}
          onChange={onChange}
          type="text"
          value={name}
        />
      </Form.Field>
      <Form.Field>
        <TextField
          name="email"
          placeholder={intl.formatMessage({
            id: "register.form.email.placeholder",
            defaultMessage: "Email",
          })}
          onChange={onChange}
          type="email"
          value={email}
        />
      </Form.Field>
      <br />

      {edit ? (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "14px" }}>
          <Checkbox
            name="isAdmin"
            checked={isAdmin}
            onChange={onCheckboxChange}
            label={<label>{intl.formatMessage({ id: "register.form.admin.label", defaultMessage: "Admin" })}</label>}
          />

          {/* Suspend */}
          <div>
            <Checkbox
              name="isSuspended"
              checked={isSuspended}
              disabled={isBanned}
              onChange={(_, d) => onSuspendChange?.(!!d.checked)}
              label={<label>{intl.formatMessage({ id: "register.form.suspend.label", defaultMessage: "Suspend" })}</label>}
            />
            <input
              type="text"
              placeholder={intl.formatMessage({ id: "register.form.suspendReason.placeholder", defaultMessage: "Suspend reason" })}
              value={suspendReason}
              disabled={!isSuspended}
              onChange={(e) => onSuspendReasonChange?.(e.target.value)}
              style={reasonInputStyle(isSuspended)}
            />
          </div>

          {/* Ban */}
          <div>
            <Checkbox
              name="isBanned"
              checked={isBanned}
              disabled={isSuspended}
              onChange={(_, d) => onBanChange?.(!!d.checked)}
              label={<label>{intl.formatMessage({ id: "register.form.ban.label", defaultMessage: "Ban" })}</label>}
            />
            <input
              type="text"
              placeholder={intl.formatMessage({ id: "register.form.banReason.placeholder", defaultMessage: "Ban reason" })}
              value={banReason}
              disabled={!isBanned}
              onChange={(e) => onBanReasonChange?.(e.target.value)}
              style={reasonInputStyle(isBanned)}
            />
          </div>
        </div>
      ) : (
        <>
          <TextField
            name="password"
            placeholder={intl.formatMessage({
              id: "register.form.password.placeholder",
              defaultMessage: "Password",
            })}
            onChange={onChange}
            type="password"
            value={password}
          />
          <TextField
            name="password2"
            placeholder={intl.formatMessage({
              id: "register.form.password2.placeholder",
              defaultMessage: "Repeat password",
            })}
            onChange={onChange}
            type="password"
            value={password2}
          />
          <Checkbox
            name="allowNewsletter"
            checked={true}
            onChange={onCheckboxChange}
            label={
              <label>
                {intl.formatMessage({
                  id: "register.form.newsletterConsent",
                  defaultMessage:
                    "I agree to receive commercial information by email...",
                })}
              </label>
            }
          />
        </>
      )}

      <div className={styles.centered}>
        <MainButton type={"submit"} className={styles.registerMainButton}>
          {edit ? (
            <FormattedMessage id="register.button.edit" defaultMessage="Edit" />
          ) : (
            <FormattedMessage id="register.button.submit" defaultMessage="Register" />
          )}
        </MainButton>
      </div>
    </Form>
  );
};

export default UserForm;